import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faSave, 
  faLock, 
  faEnvelope, 
  faPhone, 
  faShield
} from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../../components/config/apiConfig';

function AdminProfile() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    avatar: ''
  });
  
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [passwordErrors, setPasswordErrors] = useState({});
  
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        // In a real application, you would fetch the profile from the server
        // const response = await fetch(ADMIN_URLS.PROFILE, {
        //   headers: {
        //     'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        //   }
        // });
        // const data = await response.json();
        // setProfile(data);
        
        // Simulate API call with dummy data
        setTimeout(() => {
          setProfile({
            firstName: 'Admin',
            lastName: 'User',
            email: 'admin@bangalorevaluers.org',
            phone: '+91 98765 43210',
            role: 'Administrator',
            avatar: ''
          });
          setIsLoading(false);
        }, 800);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile data.');
        setIsLoading(false);
      }
    };
    
    fetchProfile();
  }, []);
  
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (passwordErrors[name]) {
      setPasswordErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };
  
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwords.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwords.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwords.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // In a real application, you would send this data to your API
      // const response = await fetch(ADMIN_URLS.PROFILE, {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      //   },
      //   body: JSON.stringify(profile)
      // });
      // if (!response.ok) throw new Error('Failed to update profile');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccessMessage('Profile updated successfully!');
      
      // Update admin name in localStorage
      localStorage.setItem('adminName', `${profile.firstName}`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      setError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    const validationErrors = validatePasswordForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setPasswordErrors(validationErrors);
      return;
    }
    
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // In a real application, you would send this data to your API
      // const response = await fetch(`${ADMIN_URLS.PROFILE}/change-password`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      //   },
      //   body: JSON.stringify({
      //     currentPassword: passwords.currentPassword,
      //     newPassword: passwords.newPassword
      //   })
      // });
      // if (!response.ok) throw new Error('Failed to change password');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccessMessage('Password changed successfully!');
      
      // Reset form
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      setError('Failed to change password. Please verify your current password.');
      console.error('Error changing password:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const renderProfileTab = () => (
    <form onSubmit={handleUpdateProfile}>
      <div className="admin-form-row">
        <div className="admin-form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={profile.firstName}
            onChange={handleProfileChange}
            className="admin-form-control"
            required
          />
        </div>
        
        <div className="admin-form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={profile.lastName}
            onChange={handleProfileChange}
            className="admin-form-control"
            required
          />
        </div>
      </div>
      
      <div className="admin-form-row">
        <div className="admin-form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={profile.email}
            onChange={handleProfileChange}
            className="admin-form-control"
            required
          />
        </div>
        
        <div className="admin-form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={profile.phone}
            onChange={handleProfileChange}
            className="admin-form-control"
          />
        </div>
      </div>
      
      <div className="admin-form-row">
        <div className="admin-form-group">
          <label htmlFor="role">Role</label>
          <input
            type="text"
            id="role"
            name="role"
            value={profile.role}
            className="admin-form-control"
            disabled
          />
          <small style={{ color: '#6c757d', marginTop: '0.25rem', display: 'block' }}>
            Contact a super administrator to change your role.
          </small>
        </div>
        
        <div className="admin-form-group">
          <label htmlFor="avatar">Profile Picture</label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              borderRadius: '50%', 
              backgroundColor: '#f0f0f0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '1rem'
            }}>
              {profile.avatar ? (
                <img 
                  src={profile.avatar} 
                  alt="Profile" 
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} 
                />
              ) : (
                <FontAwesomeIcon icon={faUser} style={{ fontSize: '1.5rem', color: '#aaa' }} />
              )}
            </div>
            <input
              type="file"
              id="avatar"
              name="avatar"
              className="admin-form-control"
              style={{ flex: '1' }}
            />
          </div>
        </div>
      </div>
      
      <div className="admin-form-actions" style={{ marginTop: '2rem' }}>
        <button
          type="submit"
          className="admin-btn admin-btn-primary"
          style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          disabled={isSaving}
        >
          <FontAwesomeIcon icon={faSave} />
          {isSaving ? 'Saving...' : 'Update Profile'}
        </button>
      </div>
    </form>
  );
  
  const renderSecurityTab = () => (
    <form onSubmit={handleChangePassword}>
      <div className="admin-form-group">
        <label htmlFor="currentPassword">Current Password</label>
        <input
          type="password"
          id="currentPassword"
          name="currentPassword"
          value={passwords.currentPassword}
          onChange={handlePasswordChange}
          className={`admin-form-control ${passwordErrors.currentPassword ? 'admin-input-error' : ''}`}
        />
        {passwordErrors.currentPassword && (
          <div className="admin-error-message">{passwordErrors.currentPassword}</div>
        )}
      </div>
      
      <div className="admin-form-row">
        <div className="admin-form-group">
          <label htmlFor="newPassword">New Password</label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={passwords.newPassword}
            onChange={handlePasswordChange}
            className={`admin-form-control ${passwordErrors.newPassword ? 'admin-input-error' : ''}`}
          />
          {passwordErrors.newPassword && (
            <div className="admin-error-message">{passwordErrors.newPassword}</div>
          )}
          <small style={{ color: '#6c757d', marginTop: '0.25rem', display: 'block' }}>
            Password must be at least 8 characters long with a mix of letters, numbers, and special characters.
          </small>
        </div>
        
        <div className="admin-form-group">
          <label htmlFor="confirmPassword">Confirm New Password</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={passwords.confirmPassword}
            onChange={handlePasswordChange}
            className={`admin-form-control ${passwordErrors.confirmPassword ? 'admin-input-error' : ''}`}
          />
          {passwordErrors.confirmPassword && (
            <div className="admin-error-message">{passwordErrors.confirmPassword}</div>
          )}
        </div>
      </div>
      
      <div className="admin-form-actions" style={{ marginTop: '2rem' }}>
        <button
          type="submit"
          className="admin-btn admin-btn-primary"
          style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          disabled={isSaving}
        >
          <FontAwesomeIcon icon={faLock} />
          {isSaving ? 'Updating...' : 'Change Password'}
        </button>
      </div>
    </form>
  );

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">My Profile</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">My Profile</span>
          </li>
        </ul>
      </div>
      
      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '0.75rem 1.25rem', 
          marginBottom: '1rem', 
          borderRadius: '0.25rem' 
        }}>
          {error}
        </div>
      )}
      
      {successMessage && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          padding: '0.75rem 1.25rem', 
          marginBottom: '1rem', 
          borderRadius: '0.25rem' 
        }}>
          {successMessage}
        </div>
      )}
      
      <div className="admin-row" style={{ display: 'flex', flexWrap: 'wrap', margin: '0 -0.75rem' }}>
        <div className="admin-col" style={{ width: '100%', padding: '0 0.75rem' }}>
          <div className="admin-card">
            <div className="admin-card-header" style={{ borderBottom: '1px solid #dee2e6' }}>
              <ul style={{ 
                display: 'flex', 
                listStyle: 'none', 
                padding: 0, 
                margin: 0,
                borderBottom: 'none' 
              }}>
                <li>
                  <button 
                    type="button"
                    onClick={() => setActiveTab('profile')}
                    style={{ 
                      padding: '0.75rem 1.25rem', 
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'profile' ? '2px solid var(--admin-primary)' : 'none',
                      color: activeTab === 'profile' ? 'var(--admin-primary)' : '#6c757d',
                      fontWeight: activeTab === 'profile' ? '600' : '400',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem' }} />
                    Personal Information
                  </button>
                </li>
                <li>
                  <button 
                    type="button"
                    onClick={() => setActiveTab('security')}
                    style={{ 
                      padding: '0.75rem 1.25rem', 
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderBottom: activeTab === 'security' ? '2px solid var(--admin-primary)' : 'none',
                      color: activeTab === 'security' ? 'var(--admin-primary)' : '#6c757d',
                      fontWeight: activeTab === 'security' ? '600' : '400',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <FontAwesomeIcon icon={faShield} style={{ marginRight: '0.5rem' }} />
                    Security
                  </button>
                </li>
              </ul>
            </div>
            
            <div className="admin-card-body">
              {isLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Loading profile data...</div>
              ) : (
                activeTab === 'profile' ? renderProfileTab() : renderSecurityTab()
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-row" style={{ display: 'flex', flexWrap: 'wrap', margin: '1.5rem -0.75rem 0' }}>
        <div className="admin-col" style={{ width: '100%', padding: '0 0.75rem' }}>
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">Contact Information</h2>
            </div>
            <div className="admin-card-body">
              <p style={{ marginBottom: '1rem' }}>If you need assistance with your account, please contact:</p>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <span>support@bangalorevaluers.org</span>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faPhone} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <span>+91 88888 77777</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminProfile;
